import React, { FunctionComponent, useEffect, useState } from 'react'
import { useClasses } from '../MyReservations.css'
import { useMyReservations } from 'components/views/my-reservations/MyReservationsProvider'
import { useTranslation } from 'react-i18next'
import { useMessage } from 'Elements/Message'
import { IUserReservationsListReservationData, useApi } from 'components/providers/ApiProvider'
import Typography from '@material-ui/core/Typography'
import ListSkeleton from 'Elements/ListSkeleton'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import ListNoRecords from 'Elements/ListNoRecords'
import ResponsiveDataTable from 'Elements/ResponsiveDataTable'
import { formatTimestamp } from 'utils'
import { format, subSeconds } from 'date-fns'
import Button from '@material-ui/core/Button'
import { Close, Refresh, Stop } from '@material-ui/icons'
import IconButton from '@material-ui/core/IconButton'

export type Props = {}
export type Functions = {}
export type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const classes = useClasses()
  const api = useApi()
  const { t } = useTranslation()
  const { showSuccess, showError } = useMessage()

  const { loadList } = useMyReservations()

  const [loading, setLoading] = useState(false)
  const [reservations, setReservations] = useState<IUserReservationsListReservationData[]>(null)

  const load = () => {
    setLoading(true)

    api.getUserReservationsList()
      .then(data => {
        setLoading(false)
        setReservations(data.reservations)
      })
      .catch(error => {
        setLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [loadList])

  const handleTerminate = (reservation: IUserReservationsListReservationData, action: string) => {
    if (!confirm(t(`confirms.${action}UserReservation`))) {
      return
    }

    api.terminateUserReservation({
      reservation_id: reservation.id,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        load()

        showSuccess(t(`messages.userReservation${action == 'cancel' ? 'Canceled' : 'Stopped'}`))
      })
      .catch(error => {
        let message

        switch (error.code) {
          default:
            message = t('alerts.tryLater')
        }

        showError(`${t(`alerts.couldNot${action == 'cancel' ? 'Cancel' : 'Stop'}UserReservation`)} ${message}`)
      })
  }

  const now = new Date()

  return (
    <>
      <Typography variant="h5" component="h1" className={classes.listTitle}>
        {t('activeReservations')}
        <IconButton
          size="small"
          color="primary"
          className={classes.refreshButton}
          onClick={load}
        >
          <Refresh />
        </IconButton>
      </Typography>

      {!reservations && (
        <ListSkeleton />
      )}

      {reservations && loading && (
        <ListLoadingIndicator />
      )}

      {reservations && !reservations.length && (
        <ListNoRecords />
      )}

      {reservations && Boolean(reservations.length) && (
        <ResponsiveDataTable
          mobileBreakpoint={959}
          table={{
            head: [
              t('zone'),
              t('period'),
              t('plateNumber'),
              '',
            ],
            rows: reservations.map(reservation => {
              const isExpired = new Date(reservation.valid_to).getTime() <= now.getTime()

              return {
                cells: [
                  {
                    value: <span style={{ ...(isExpired && { color: '#aaa' }) }}>
                      {reservation.zone.code}
                    </span>,
                  },
                  {
                    value: <span style={{ ...(isExpired && { color: '#aaa' }) }}>
                      {(new Date(reservation.valid_to).getTime() - new Date(reservation.valid_from).getTime()) == 1000 * 60 * 60 * 24 && formatTimestamp(reservation.valid_from, 'HH:mm') == '00:00' ?
                        formatTimestamp(reservation.valid_from, 'dd.MM.yyyy')
                        :
                        `${formatTimestamp(reservation.valid_from)} - ${
                          formatTimestamp(reservation.valid_to, 'HH:mm') == '00:00' ?
                            format(subSeconds(new Date(reservation.valid_to), 1), 'dd.MM.yyyy HH:mm') :
                            formatTimestamp(reservation.valid_to)
                        }`}
                    </span>,
                  },
                  {
                    value: <span style={{ fontFamily: '"Roboto Mono", monospace', ...(isExpired && { color: '#aaa' }) }}>
                      {reservation.plate_number}
                    </span>,
                  },
                  {
                    value: !isExpired && <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      startIcon={new Date(reservation.valid_from) > now ? <Close /> : <Stop />}
                      onClick={() => handleTerminate(reservation, new Date(reservation.valid_from) > now ? 'cancel' : 'stop')}
                    >
                      {t(new Date(reservation.valid_from) > now ? 'cancel' : 'stop')}
                    </Button>,
                    align: 'right',
                    hiddenOnMobile: isExpired,
                  },
                ],
              }
            }),
          }}
        />
      )}
    </>
  )
}

export default List
